/**
 * Metro - carriage interior at window height.
 * Ceiling strips travel overhead; platform lights sweep past the glass.
 */

import { createRng, drawGrain, drawVignette, glow, reactive, withAlpha } from './shared.js';

const WORLD = (() => {
  const rng = createRng(0x3f9d1);

  const ceilingLights = Array.from({ length: 6 }, (_, i) => ({
    offset: i / 6,
    cycles: 1,
    width: 0.6 + rng() * 0.3,
    brightness: 0.55 + rng() * 0.35,
  }));

  const sweeps = Array.from({ length: 22 }, () => ({
    ny: rng(),
    cycles: 1 + Math.floor(rng() * 4),
    offset: rng(),
    length: 0.1 + rng() * 0.4,
    thickness: 0.003 + rng() * 0.01,
    warm: rng(),
    alpha: 0.3 + rng() * 0.6,
  }));

  const panels = Array.from({ length: 9 }, () => ({
    cycles: 1 + Math.floor(rng() * 3),
    offset: rng(),
    width: 0.08 + rng() * 0.2,
    alpha: 0.12 + rng() * 0.2,
  }));

  return { ceilingLights, sweeps, panels };
})();

export default {
  id: 'metro',
  label: 'Metro',
  description: 'Procedural metro carriage: overhead strip lights and sweeping platform light.',
  loopable: true,

  draw(ctx, { t, loopSeconds, w, h, audio, strength }) {
    const phase = (t % loopSeconds) / loopSeconds;
    const bass = reactive(audio.bass, strength);
    const level = reactive(audio.level, strength);

    ctx.fillStyle = '#080A0E';
    ctx.fillRect(0, 0, w, h);

    const swayX = Math.sin(phase * Math.PI * 2) * w * 0.0035 * (1 + bass * 0.6);
    const tilt = Math.sin(phase * Math.PI * 2 + 0.6) * 0.0035 * (1 + bass * 0.6);
    ctx.save();
    ctx.translate(w / 2 + swayX, h / 2);
    ctx.rotate(tilt);
    ctx.translate(-w / 2, -h / 2);

    // --- ceiling
    const ceil = ctx.createLinearGradient(0, 0, 0, h * 0.3);
    ceil.addColorStop(0, '#0B0D12');
    ceil.addColorStop(0.6, '#151A22');
    ceil.addColorStop(1, '#0C0F14');
    ctx.fillStyle = ceil;
    ctx.fillRect(0, 0, w, h * 0.3);

    // --- ceiling strip lights travelling overhead
    for (const light of WORLD.ceilingLights) {
      const travel = (light.offset + phase * light.cycles) % 1;
      // easing makes the lights accelerate as they pass the camera
      const y = h * 0.02 + Math.pow(travel, 1.6) * h * 0.3;
      const bandW = light.width * w;
      const x = w / 2 - bandW / 2 + Math.sin(phase * Math.PI * 2 + light.offset * 6) * w * 0.02;
      const fade = Math.sin(travel * Math.PI);
      const gradient = ctx.createLinearGradient(0, y, 0, y + h * 0.02);
      gradient.addColorStop(0, withAlpha('#F2F6FF', 0));
      gradient.addColorStop(0.5, withAlpha('#F2F6FF', light.brightness * fade * (0.8 + level * 0.3)));
      gradient.addColorStop(1, withAlpha('#F2F6FF', 0));
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, bandW, h * 0.02);
      glow(ctx, x + bandW / 2, y + h * 0.01, bandW * 0.5, '#DCE8FF', 0.12 * fade * (0.7 + level * 0.5));
    }

    // --- window band
    const winTop = h * 0.3;
    const winBottom = h * 0.72;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, winTop, w, winBottom - winTop);
    ctx.clip();

    const outside = ctx.createLinearGradient(0, winTop, 0, winBottom);
    outside.addColorStop(0, '#0A0E15');
    outside.addColorStop(0.5, '#111722');
    outside.addColorStop(1, '#070A0F');
    ctx.fillStyle = outside;
    ctx.fillRect(0, winTop, w, winBottom - winTop);

    // blurred platform panels sweeping past
    for (const p of WORLD.panels) {
      const travel = (p.offset + phase * p.cycles) % 1.2;
      const x = (1.2 - travel) * w * 1.2 - w * 0.2;
      ctx.fillStyle = withAlpha('#3C4A63', p.alpha * (0.8 + level * 0.4));
      ctx.fillRect(x, winTop + (winBottom - winTop) * 0.15, p.width * w, (winBottom - winTop) * 0.7);
    }

    // sweeping light streaks
    for (const s of WORLD.sweeps) {
      const travel = (s.offset + phase * s.cycles) % 1.15;
      const x = (1.15 - travel) * w * 1.15 - w * 0.15;
      const y = winTop + s.ny * (winBottom - winTop);
      const len = s.length * w;
      const color = s.warm > 0.65 ? '#FFC27A' : s.warm > 0.35 ? '#EAF1FF' : '#A8C4FF';
      const gradient = ctx.createLinearGradient(x, 0, x + len, 0);
      gradient.addColorStop(0, withAlpha(color, 0));
      gradient.addColorStop(0.4, withAlpha(color, s.alpha * (0.8 + level * 0.4)));
      gradient.addColorStop(1, withAlpha(color, 0));
      ctx.fillStyle = gradient;
      ctx.fillRect(x, y, len, Math.max(1.2, s.thickness * (winBottom - winTop)));
    }

    // glass reflection of the interior strips
    const refl = ctx.createLinearGradient(0, winTop, 0, winBottom);
    refl.addColorStop(0, `rgba(200,220,255,${0.05 + level * 0.04})`);
    refl.addColorStop(0.35, 'rgba(200,220,255,0)');
    refl.addColorStop(1, 'rgba(160,190,255,0.03)');
    ctx.fillStyle = refl;
    ctx.fillRect(0, winTop, w, winBottom - winTop);
    ctx.restore();

    // --- window frame + mullions
    ctx.strokeStyle = '#05070B';
    ctx.lineWidth = Math.max(8, h * 0.012);
    ctx.beginPath();
    ctx.moveTo(0, winTop);
    ctx.lineTo(w, winTop);
    ctx.moveTo(0, winBottom);
    ctx.lineTo(w, winBottom);
    ctx.stroke();

    // --- seat backs along the bottom
    const seat = ctx.createLinearGradient(0, winBottom, 0, h);
    seat.addColorStop(0, '#12161D');
    seat.addColorStop(0.35, '#0C0F15');
    seat.addColorStop(1, '#05070A');
    ctx.fillStyle = seat;
    ctx.fillRect(0, winBottom, w, h - winBottom);

    for (let i = 0; i < 3; i += 1) {
      const bx = w * (0.04 + i * 0.33);
      const bw = w * 0.28;
      ctx.fillStyle = '#0A0D12';
      ctx.beginPath();
      ctx.moveTo(bx, h);
      ctx.lineTo(bx + bw * 0.06, winBottom + h * 0.02);
      ctx.lineTo(bx + bw * 0.94, winBottom + h * 0.02);
      ctx.lineTo(bx + bw, h);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.035)';
      ctx.lineWidth = Math.max(1, w * 0.0025);
      ctx.stroke();
    }

    // handrail
    ctx.strokeStyle = 'rgba(220,230,245,0.10)';
    ctx.lineWidth = Math.max(2, w * 0.006);
    ctx.beginPath();
    ctx.moveTo(0, winBottom - h * 0.02);
    ctx.lineTo(w, winBottom - h * 0.02);
    ctx.stroke();

    ctx.restore();

    drawVignette(ctx, w, h, 0.58);
    drawGrain(ctx, w, h, { t, alpha: 0.03 });
  },
};
